import {
  JAPAN_PUBLIC_HOLIDAYS,
  JAPAN_PUBLIC_HOLIDAYS_EN,
} from "@/config/japanPublicHolidays";
import {buildPayrollAttendanceBook} from "@/utils/payrollAttendanceBook";
import {
  buildPayrollFlagsByUserId,
  payrollFlagsForMember,
} from "@/utils/payrollMemberFlags";
import {normalizeTimesheetRowUserId} from "@/utils/timesheetRosterLookup";

function parseRowHours(row) {
  for (const key of ["hours_with_timer", "working_hours", "hours"]) {
    const raw = row[key];
    if (raw == null || raw === "") continue;
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
}

function decimalHoursToDecimalString(decimalHours) {
  const h = Number(decimalHours);
  if (!Number.isFinite(h) || h <= 0) return "";
  return (Math.round(h * 100) / 100).toFixed(2);
}

function buildPayrollExportPackages({
  monthKey,
  empReportRows,
  allItems,
  projectNameById,
  payrollFlagsByUserId,
}) {
  const itemsByUserId = {};
  (allItems || []).forEach((row) => {
    const uid = normalizeTimesheetRowUserId(row);
    if (uid == null) return;
    if (!itemsByUserId[uid]) itemsByUserId[uid] = [];
    itemsByUserId[uid].push(row);
  });

  return (empReportRows || []).map((rosterRow) => {
    const userItems =
      rosterRow.userId != null ? itemsByUserId[rosterRow.userId] || [] : [];
    const payrollOpts =
      rosterRow.userId != null
        ? payrollFlagsByUserId[rosterRow.userId]
        : payrollFlagsForMember(null, rosterRow.empOutId);
    const attendanceBook = buildPayrollAttendanceBook(
      monthKey,
      userItems,
      projectNameById,
      payrollOpts
    );
    return {rosterRow, userItems, payrollOpts, attendanceBook};
  });
}

function writeHoursSummarySheet(workbook, monthKey, exportPackages) {
  const ws = workbook.addWorksheet("Hours Summary");
  const headers = [
    "Name",
    "Full Name",
    "EMP/Out No.",
    "NekoHR matched name",
    "Month",
    "勤務時間合計 (HH:MM)",
    "Total Hours (decimal)",
    "Days Worked",
    "PTO Days",
    "PTO Hours (HH:MM)",
    "月給",
    "Monthly Target (h)",
    "Timesheet Lines",
    "Export basis",
  ];
  headers.forEach((label, idx) => {
    const cell = ws.getCell(1, idx + 1);
    cell.value = label;
    styleHeaderCell(cell, COLORS.empHeaderFill);
  });

  exportPackages.forEach((pkg, rowIdx) => {
    const r = rowIdx + 2;
    const {rosterRow, payrollOpts, attendanceBook} = pkg;
    const s = attendanceBook.summary || {};
    const monthly = payrollOpts && payrollOpts.monthlyTreatment;
    const basis = monthly
      ? "Monthly distributed (月給)"
      : "Logged timesheets";
    const values = [
      rosterRow.name || "",
      rosterRow.fullName || "",
      rosterRow.empOutId || "",
      rosterRow.nekohrName || "",
      monthKey,
      s.totalWorkTime || "0:00",
      decimalHoursToDecimalString(s.totalWorkHoursDecimal),
      s.daysWorked ?? 0,
      s.ptoDays ?? 0,
      s.ptoTime || "0:00",
      monthly ? "Y" : "",
      monthly ? payrollOpts.monthlyTargetHours : "",
      rosterRow.timesheetLineCount ?? "",
      basis,
    ];
    values.forEach((val, colIdx) => {
      const cell = ws.getCell(r, colIdx + 1);
      if (colIdx === 5 || colIdx === 9) {
        setDurationCell(cell, String(val || "0:00"));
      } else {
        cell.value = val;
      }
      setCellBorder(cell);
    });
  });

  ws.columns = [
    {width: 12},
    {width: 22},
    {width: 16},
    {width: 18},
    {width: 10},
    {width: 16},
    {width: 14},
    {width: 12},
    {width: 10},
    {width: 14},
    {width: 6},
    {width: 14},
    {width: 14},
    {width: 24},
  ];
  ws.views = [{state: "frozen", ySplit: 1}];
}

function writeAttendanceDetailSheet(workbook, monthKey, exportPackages) {
  const ws = workbook.addWorksheet("Attendance Detail");
  const headers = [
    "Month",
    "Name",
    "EMP/Out No.",
    "Date",
    "Weekday",
    "Work Hours (decimal)",
    "Work Hours (HH:MM)",
    "Start",
    "Finish",
    "Leave",
    "Leave Hours",
    "Source",
  ];
  headers.forEach((label, idx) => {
    const cell = ws.getCell(1, idx + 1);
    cell.value = label;
    styleHeaderCell(cell, COLORS.headerFill);
  });

  let r = 2;
  exportPackages.forEach((pkg) => {
    const {rosterRow, payrollOpts, attendanceBook, userItems} = pkg;
    const monthly = payrollOpts && payrollOpts.monthlyTreatment;
    const byDate = {};
    (userItems || []).forEach((row) => {
      const dk = String(row.log_date || "").slice(0, 10);
      if (!dk) return;
      byDate[dk] = (byDate[dk] || 0) + parseRowHours(row);
    });

    (attendanceBook.dailyRows || []).forEach((day) => {
      const logged = byDate[day.dateKey] || 0;
      let source = "";
      if (day.leaveLabel && day.workHoursDecimal <= 0) {
        source = "pto";
      } else if (monthly && day.workHoursDecimal > 0 && logged <= 0) {
        source = "monthly distributed";
      } else if (day.workHoursDecimal > 0) {
        source = logged > 0 ? "timesheet" : "computed";
      }
      if (!source && day.isWeekend) source = "weekend";
      if (!source && day.isHoliday) source = "holiday";

      const rowValues = [
        monthKey,
        rosterRow.name || "",
        rosterRow.empOutId || "",
        day.dateKey,
        day.weekday || "",
        day.workHoursDecimal > 0 ? day.workHoursDecimal : "",
        day.totalTime || "",
        day.startTime || "",
        day.finishTime || "",
        day.leaveLabel || "",
        day.leaveHoursLabel || "",
        source,
      ];
      rowValues.forEach((val, colIdx) => {
        const cell = ws.getCell(r, colIdx + 1);
        cell.value = val;
        setCellBorder(cell);
        if (colIdx === 3 && day.dateKey) {
          cell.value = dateKeyToExcelDate(day.dateKey);
          cell.numFmt = FMT_DATE_JA;
        }
        if (colIdx === 6 && day.totalTime) {
          setDurationCell(cell, day.totalTime);
        }
      });
      r += 1;
    });
  });

  ws.columns = [
    {width: 10},
    {width: 12},
    {width: 16},
    {width: 14},
    {width: 10},
    {width: 14},
    {width: 12},
    {width: 8},
    {width: 8},
    {width: 14},
    {width: 10},
    {width: 18},
  ];
  ws.views = [{state: "frozen", ySplit: 1}];
}

function writeTimesheetSourceSheet(
  workbook,
  monthKey,
  allItems,
  exportPackages,
  projectNameById
) {
  const ws = workbook.addWorksheet("Timesheet Source");
  const includedUserIds = new Set(
    exportPackages
      .map((pkg) => pkg.rosterRow.userId)
      .filter((uid) => uid != null)
  );
  const empOutByUserId = {};
  exportPackages.forEach((pkg) => {
    if (pkg.rosterRow.userId != null) {
      empOutByUserId[pkg.rosterRow.userId] = pkg.rosterRow;
    }
  });

  const headers = [
    "Month",
    "Date",
    "Name",
    "EMP/Out No.",
    "User ID",
    "Project",
    "Hours (decimal)",
    "Description",
  ];
  headers.forEach((label, idx) => {
    const cell = ws.getCell(1, idx + 1);
    cell.value = label;
    styleHeaderCell(cell, COLORS.headerFill);
  });

  const rows = (allItems || [])
    .filter((row) => {
      const uid = normalizeTimesheetRowUserId(row);
      const dk = String(row.log_date || "").slice(0, 7);
      return uid != null && includedUserIds.has(uid) && dk === monthKey;
    })
    .sort((a, b) => {
      const da = String(a.log_date || "");
      const db = String(b.log_date || "");
      if (da !== db) return da.localeCompare(db);
      return (normalizeTimesheetRowUserId(a) || 0) -
        (normalizeTimesheetRowUserId(b) || 0);
    });

  rows.forEach((row, rowIdx) => {
    const r = rowIdx + 2;
    const uid = normalizeTimesheetRowUserId(row);
    const roster = empOutByUserId[uid] || {};
    const dateKey = String(row.log_date || "").slice(0, 10);
    const hours = parseRowHours(row);
    const projectName =
      projectNameById[row.project_id] ||
      row.project_name ||
      row.project ||
      "";

    ws.getCell(r, 1).value = monthKey;
    const dateCell = ws.getCell(r, 2);
    if (dateKey) {
      dateCell.value = dateKeyToExcelDate(dateKey);
      dateCell.numFmt = FMT_DATE_JA;
    }
    ws.getCell(r, 3).value = roster.name || "";
    ws.getCell(r, 4).value = roster.empOutId || "";
    ws.getCell(r, 5).value = uid;
    ws.getCell(r, 6).value = projectName;
    ws.getCell(r, 7).value = hours > 0 ? hours : "";
    ws.getCell(r, 8).value =
      row.log_description || row.task_description || "";
    for (let c = 1; c <= headers.length; c++) {
      setCellBorder(ws.getCell(r, c));
    }
  });

  ws.columns = [
    {width: 10},
    {width: 14},
    {width: 12},
    {width: 16},
    {width: 10},
    {width: 24},
    {width: 12},
    {width: 36},
  ];
  ws.views = [{state: "frozen", ySplit: 1}];
}

const FMT_DATE_JA = 'yyyy"年"m"月"d"日"';
const FMT_CLOCK = "h:mm";
const FMT_DURATION = "[h]:mm";
const FMT_CHECK = "0.00";

const COLORS = {
  titleFill: "FFD9E2F3",
  headerFill: "FFBDD7EE",
  totalHeaderFill: "FF8EA9DB",
  weekendFill: "FFF8CBAD",
  holidayFill: "FFFFF2CC",
  empHeaderFill: "FFD9E2F3",
};

const ATTENDANCE_HEADERS = [
  "日付",
  "曜日",
  "勤務開始時刻",
  "勤務終了時刻",
  "休憩開始時間",
  "休憩終了時間",
  "合計勤務時間",
  "休暇",
  "休暇時間",
  "Check",
];

const THIN_BORDER = {
  top: {style: "thin", color: {argb: "FF000000"}},
  left: {style: "thin", color: {argb: "FF000000"}},
  bottom: {style: "thin", color: {argb: "FF000000"}},
  right: {style: "thin", color: {argb: "FF000000"}},
};

function solidFill(argb) {
  return {type: "pattern", pattern: "solid", fgColor: {argb}};
}

function clockTimeToExcelFraction(timeLabel) {
  if (!timeLabel) return null;
  const [hh, mm] = String(timeLabel).split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return (hh * 60 + mm) / (24 * 60);
}

function durationToExcelFraction(durationLabel) {
  if (durationLabel == null || durationLabel === "") return null;
  const [hh, mm] = String(durationLabel).split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return (hh * 60 + mm) / (24 * 60);
}

/** YYYY-MM-DD → Excel date cell (UTC noon avoids JST −1 day in xlsx serials). */
function dateKeyToExcelDate(dateKey) {
  const [y, mo, d] = String(dateKey).split("-").map(Number);
  if (!y || !mo || !d) return null;
  return new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
}

function sanitizeSheetName(raw) {
  return String(raw || "Sheet")
    .replace(/[\\/?*[\]:]/g, "")
    .trim()
    .slice(0, 31);
}

function uniqueSheetName(base, empOutId, used) {
  let name =
    sanitizeSheetName(base) || sanitizeSheetName(empOutId) || "Employee";
  let candidate = name;
  let n = 2;
  while (used.has(candidate)) {
    const suffix = ` ${n}`;
    candidate = name.slice(0, Math.max(1, 31 - suffix.length)) + suffix;
    n += 1;
  }
  used.add(candidate);
  return candidate;
}

function setCellBorder(cell) {
  cell.border = THIN_BORDER;
}

function borderRange(ws, rowStart, rowEnd, colStart, colEnd) {
  for (let r = rowStart; r <= rowEnd; r++) {
    for (let c = colStart; c <= colEnd; c++) {
      setCellBorder(ws.getCell(r, c));
    }
  }
}

function setClockCell(cell, timeLabel) {
  const v = clockTimeToExcelFraction(timeLabel);
  if (v == null) return;
  cell.value = v;
  cell.numFmt = FMT_CLOCK;
}

function setDurationCell(cell, durationLabel) {
  const v = durationToExcelFraction(durationLabel);
  if (v == null) return;
  cell.value = v;
  cell.numFmt = FMT_DURATION;
}

/** Excel [h]:mm from decimal hours (monthly totals can exceed 24h). */
function setDecimalHoursDurationCell(cell, decimalHours) {
  const h = Number(decimalHours);
  if (!Number.isFinite(h) || h < 0) return;
  cell.value = h / 24;
  cell.numFmt = FMT_DURATION;
}

function styleHeaderCell(cell, fillArgb, bold = true) {
  cell.font = {bold, size: 11};
  cell.fill = solidFill(fillArgb);
  cell.alignment = {horizontal: "center", vertical: "middle", wrapText: true};
  setCellBorder(cell);
}

function downloadWorkbookBuffer(buffer, filename) {
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function writeEmpSheet(workbook, empReportRows) {
  const ws = workbook.addWorksheet("EMP");
  const headers = [
    "Name",
    "Name in Koneko",
    "Contract Type",
    "Status",
    "Emp & Out No.",
    "Full Name",
    "EMP/Out No.",
    "",
    "",
  ];
  headers.forEach((label, idx) => {
    const cell = ws.getCell(1, idx + 1);
    cell.value = label;
    styleHeaderCell(cell, COLORS.empHeaderFill);
  });
  (empReportRows || []).forEach((row, rowIdx) => {
    const r = rowIdx + 2;
    const values = [
      row.name || "",
      row.nameInKoneko || "",
      row.contractType || "",
      row.status || "",
      row.empOutNo || "",
      row.fullName || "",
      row.empOutId || "",
      row.roleNote || "",
      row.notes || "",
    ];
    values.forEach((val, colIdx) => {
      const cell = ws.getCell(r, colIdx + 1);
      cell.value = val;
      setCellBorder(cell);
      cell.alignment = {vertical: "top", wrapText: true};
    });
  });
  ws.columns = [
    {width: 12},
    {width: 18},
    {width: 28},
    {width: 14},
    {width: 14},
    {width: 24},
    {width: 16},
    {width: 12},
    {width: 16},
  ];
}

function writeHolidaysSheet(workbook) {
  const ws = workbook.addWorksheet(
    sanitizeSheetName("祝日・日付") || "Holidays"
  );
  const headers = [
    "祝日（YYYY/M/D 形式で入力）",
    "",
    "",
    "Leave types (reference)",
  ];
  headers.forEach((label, idx) => {
    const cell = ws.getCell(1, idx + 1);
    cell.value = label;
    styleHeaderCell(cell, COLORS.headerFill);
  });
  const leaveTypes = [
    "有給休暇",
    "半日休暇",
    "無給休暇",
    "特別休暇",
    "傷病休業",
    "産前産後休業",
    "育児休業",
    "介護休業",
    "時短勤務",
    "振替休日",
  ];
  const keys = Object.keys(JAPAN_PUBLIC_HOLIDAYS).sort();
  keys.forEach((dateKey, idx) => {
    const r = idx + 2;
    const dateCell = ws.getCell(r, 1);
    dateCell.value = dateKeyToExcelDate(dateKey);
    dateCell.numFmt = FMT_DATE_JA;
    setCellBorder(dateCell);
    ws.getCell(r, 2).value = JAPAN_PUBLIC_HOLIDAYS[dateKey];
    ws.getCell(r, 3).value = JAPAN_PUBLIC_HOLIDAYS_EN[dateKey] || "";
    ws.getCell(r, 4).value = leaveTypes[idx] || "";
    borderRange(ws, r, r, 1, 4);
  });
  ws.columns = [{width: 18}, {width: 16}, {width: 24}, {width: 18}];
}

function writeEmployeeAttendanceSheet(
  workbook,
  sheetName,
  rosterRow,
  attendanceBook
) {
  const ws = workbook.addWorksheet(sheetName);
  const colCount = ATTENDANCE_HEADERS.length;
  const displayName =
    rosterRow.fullName || rosterRow.nameInKoneko || rosterRow.name || "";

  ws.mergeCells(1, 1, 1, colCount);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = "出勤簿";
  titleCell.font = {bold: true, size: 14};
  titleCell.fill = solidFill(COLORS.titleFill);
  titleCell.alignment = {horizontal: "center", vertical: "middle"};
  borderRange(ws, 1, 1, 1, colCount);

  ws.getCell(2, 1).value = "氏名";
  ws.getCell(2, 1).font = {bold: true};
  ws.getCell(2, 2).value = displayName;
  ws.getCell(2, 4).value = rosterRow.empOutId || "";
  ws.getCell(2, 9).value = "8時間/日";
  borderRange(ws, 2, 2, 1, colCount);

  ATTENDANCE_HEADERS.forEach((label, idx) => {
    const cell = ws.getCell(3, idx + 1);
    cell.value = label;
    const fill = idx === 6 ? COLORS.totalHeaderFill : COLORS.headerFill;
    styleHeaderCell(cell, fill);
  });

  const dailyRows = attendanceBook.dailyRows || [];
  dailyRows.forEach((day, idx) => {
    const r = 4 + idx;
    const dateCell = ws.getCell(r, 1);
    dateCell.value = dateKeyToExcelDate(day.dateKey);
    dateCell.numFmt = FMT_DATE_JA;

    ws.getCell(r, 2).value = day.weekday || "";
    ws.getCell(r, 2).alignment = {horizontal: "center"};

    setClockCell(ws.getCell(r, 3), day.startTime);
    setClockCell(ws.getCell(r, 4), day.finishTime);
    setClockCell(ws.getCell(r, 5), day.breakStart);
    setClockCell(ws.getCell(r, 6), day.breakEnd);

    if (day.totalTime != null && day.totalTime !== "") {
      setDurationCell(ws.getCell(r, 7), day.totalTime);
    } else if ((Number(day.workHoursDecimal) || 0) > 0) {
      setDecimalHoursDurationCell(ws.getCell(r, 7), day.workHoursDecimal);
    }

    ws.getCell(r, 8).value = day.leaveLabel || null;
    if (day.leaveHoursLabel) {
      setDurationCell(ws.getCell(r, 9), day.leaveHoursLabel);
    }

    const checkCell = ws.getCell(r, 10);
    checkCell.value = 0;
    checkCell.numFmt = FMT_CHECK;

    let rowFill = null;
    if (day.isWeekend) rowFill = COLORS.weekendFill;
    else if (day.isHoliday && !day.workHoursDecimal)
      rowFill = COLORS.holidayFill;

    for (let c = 1; c <= colCount; c++) {
      const cell = ws.getCell(r, c);
      if (rowFill) cell.fill = solidFill(rowFill);
      if (c >= 3 && c <= 7) cell.alignment = {horizontal: "center"};
      setCellBorder(cell);
    }
  });

  const summaryStart = 4 + dailyRows.length + 1;
  const summaryLabelCol = 8;
  const summaryValueCol = 9;
  const s = attendanceBook.summary || {};
  const summaryRows = [
    ["勤務時間合計", null, "duration-sum"],
    ["出勤日数", s.daysWorked ?? 0, "int"],
    ["休業日数", s.closedDays ?? 0, "int"],
    ["有給取得（日数換算）", s.ptoDays ?? 0, "int"],
    ["有給時間", s.ptoTime || "0:00", "duration"],
  ];

  summaryRows.forEach((entry, idx) => {
    const r = summaryStart + idx;
    const labelCell = ws.getCell(r, summaryLabelCol);
    labelCell.value = entry[0];
    labelCell.alignment = {
      horizontal: "right",
      vertical: "middle",
      wrapText: true,
    };

    const valueCell = ws.getCell(r, summaryValueCol);
    if (entry[2] === "duration-sum") {
      // Plain HH:MM text — monthly totals exceed 24h; Excel time cells wrap as 1:28 instead of 49:29.
      valueCell.value = s.totalWorkTime || "0:00";
    } else if (entry[2] === "duration") {
      valueCell.value = entry[1] || "0:00";
    } else {
      valueCell.value = entry[1];
      valueCell.numFmt = "0";
    }
    valueCell.alignment = {horizontal: "center", vertical: "middle"};

    borderRange(ws, r, r, summaryLabelCol, summaryValueCol);
  });

  ws.columns = [
    {width: 17},
    {width: 11},
    {width: 11},
    {width: 11},
    {width: 11},
    {width: 11},
    {width: 12},
    {width: 14},
    {width: 10},
    {width: 8},
  ];
  ws.views = [{state: "frozen", ySplit: 3}];
}

export function payrollWorkbookExportFilename(monthKey) {
  return `${monthKey} 出勤簿 Employee and Outsourcer.xlsx`;
}

/**
 * Build the full-month payroll workbook (.xlsx) in memory.
 * @returns {Promise<{ buffer: ArrayBuffer, filename: string }>}
 */
export async function buildPayrollWorkbookBuffer({
  monthKey,
  empReportRows,
  allItems,
  projectNameById,
  teamRows,
}) {
  const payrollFlagsByUserId = buildPayrollFlagsByUserId(
    teamRows,
    empReportRows
  );
  const ExcelJS = (await import(/* webpackChunkName: "exceljs" */ "exceljs"))
    .default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "NekoHR Payroll";
  workbook.created = new Date();

  writeEmpSheet(workbook, empReportRows);
  writeHolidaysSheet(workbook);

  const exportPackages = buildPayrollExportPackages({
    monthKey,
    empReportRows,
    allItems,
    projectNameById,
    payrollFlagsByUserId,
  });

  writeHoursSummarySheet(workbook, monthKey, exportPackages);
  writeAttendanceDetailSheet(workbook, monthKey, exportPackages);
  writeTimesheetSourceSheet(
    workbook,
    monthKey,
    allItems,
    exportPackages,
    projectNameById
  );

  const usedSheetNames = new Set([
    "EMP",
    sanitizeSheetName("祝日・日付"),
    "Hours Summary",
    "Attendance Detail",
    "Timesheet Source",
  ]);

  exportPackages.forEach(({rosterRow, attendanceBook}) => {
    const sheetName = uniqueSheetName(
      rosterRow.name,
      rosterRow.empOutId,
      usedSheetNames
    );
    writeEmployeeAttendanceSheet(
      workbook,
      sheetName,
      rosterRow,
      attendanceBook
    );
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return {
    buffer,
    filename: payrollWorkbookExportFilename(monthKey),
  };
}

/**
 * Build and download the full-month payroll workbook (.xlsx).
 */
export async function exportPayrollWorkbook(params) {
  const {buffer, filename} = await buildPayrollWorkbookBuffer(params);
  downloadWorkbookBuffer(buffer, filename);
}
